/**
 * ModeMasterToggle — `<provider> default` vs TrafficPulse Optimisation mode.
 */

import { FC } from 'react';
import { matchTracker, trackerLabel, type FlowOptimizerMode } from '@trafficpulse/shared';

import { Switch } from '@/components/ui/form/controls/Switch';
import type { FlowOptimizerProvider } from '../services/flow-optimizer-api';

export interface ModeMasterToggleProps {
  mode: FlowOptimizerMode;
  onChange: (mode: FlowOptimizerMode) => void;
  disabled?: boolean;
  /** When false, hide the built-in "Mode" caption (e.g. toolbar supplies a column label). */
  hideLabel?: boolean;
  /** Defaults to 'voluum' for back-compat. Drives the left-label text. */
  provider?: FlowOptimizerProvider;
}

const TRAFFICPULSE_OPTIMISATION = 'TrafficPulse Optimisation';

const defaultHint = (provider: FlowOptimizerProvider): string =>
  matchTracker(provider, {
    voluum: () => 'Match flow behavior to the Voluum campaign payload (Standard tab only).',
    binom: () => 'Match rotation behavior to the Binom campaign payload (Standard tab only).',
    'tres-commas': () =>
      'Match flow behavior to the Tres Commas flow payload (Standard tab only).',
  });

const TRAFFICPULSE_HINT =
  'Turn on Rules and Formula engines in addition to the Standard editor.';

const ModeMasterToggle: FC<ModeMasterToggleProps> = ({
  mode,
  onChange,
  disabled,
  hideLabel = false,
  provider = 'voluum',
}) => {
  const isCustom = mode === 'CUSTOM';
  const defaultLabel = `${trackerLabel(provider)} default`;
  return (
    <div className="flex items-center gap-2">
      {!hideLabel && (
        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Mode
        </span>
      )}
      <div
        className="flex items-center gap-1.5"
        title={isCustom ? TRAFFICPULSE_HINT : defaultHint(provider)}
      >
        <span
          className={
            isCustom
              ? 'whitespace-nowrap text-[10px] text-muted-foreground sm:text-[11px]'
              : 'whitespace-nowrap text-[10px] font-semibold text-foreground sm:text-[11px]'
          }
        >
          {defaultLabel}
        </span>
        <Switch
          checked={isCustom}
          onCheckedChange={(next) => onChange(next ? 'CUSTOM' : 'STANDARD')}
          disabled={disabled}
          aria-label={`Toggle between ${defaultLabel} and ${TRAFFICPULSE_OPTIMISATION}`}
        />
        <span
          className={
            isCustom
              ? 'max-w-[min(15rem,48vw)] whitespace-normal text-left text-[10px] font-semibold leading-tight text-foreground sm:max-w-none sm:whitespace-nowrap sm:text-[11px]'
              : 'whitespace-nowrap text-[10px] text-muted-foreground sm:text-[11px]'
          }
        >
          {TRAFFICPULSE_OPTIMISATION}
        </span>
      </div>
    </div>
  );
};

export default ModeMasterToggle;
